import { useTranslation } from "react-i18next";
import { AlertTriangle, X } from "lucide-react";

import Button from "../../../../shared/ui/button/Button";

interface PaymentIssueBannerProps {
    /** Defaults to the declined renewal's; the expiring card brings its own. */
    title?: string;
    /** What failed and what is still held; the therapist's own or the centre's. */
    message: string;
    onUpdateCard: () => void;
    isBusy?: boolean;
    /** Offered where the banner follows somebody around the app. */
    onDismiss?: () => void;
}

/**
 * A renewal was declined and Stripe is retrying it — or is about to go to a
 * card that will have expired.
 *
 * Nothing is lost yet — `past_due` keeps the plan — so this is a warning,
 * not an error, and its one action is the one that fixes it: the card.
 */
export default function PaymentIssueBanner({
    title,
    message,
    onUpdateCard,
    isBusy = false,
    onDismiss,
}: PaymentIssueBannerProps) {
    const { t } = useTranslation();
    return (
        <div
            role="alert"
            className="flex flex-col gap-3 rounded-xl bg-amber-50 p-4 text-sm text-amber-900 sm:flex-row sm:items-center dark:bg-amber-500/10 dark:text-amber-200"
        >
            <AlertTriangle size={18} className="shrink-0" />
            <div className="flex-1">
                <p className="font-semibold">{title ?? t("subscription.billing.dunning.title")}</p>
                <p>{message}</p>
            </div>
            <div className="flex items-center gap-2">
                <Button size="sm" onClick={onUpdateCard} disabled={isBusy}>
                    {t("subscription.billing.dunning.update")}
                </Button>
                {onDismiss && (
                    <button
                        type="button"
                        onClick={onDismiss}
                        aria-label={t("subscription.billing.dunning.dismiss")}
                        className="rounded-lg p-1.5 hover:bg-amber-100 dark:hover:bg-amber-500/20"
                    >
                        <X size={16} />
                    </button>
                )}
            </div>
        </div>
    );
}
